import { ReactNode, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import {
  Building2,
  LayoutDashboard,
  Users,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Boxes,
  FlaskConical,
  Globe,
  FileText,
  Bell,
  ChevronLeft,
  ChevronRight,
  Activity,
  BarChart3,
  Package,
  Megaphone,
  Key,
} from 'lucide-react';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';

interface AdminLayoutProps {
  children: ReactNode;
}

const adminNavigation = [
  {
    section: 'Overview',
    items: [
      { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Activity Log', href: '/admin/activity', icon: Activity },
    ],
  },
  {
    section: 'Customers',
    items: [
      { name: 'Companies', href: '/admin/companies', icon: Building2 },
      { name: 'Users', href: '/admin/users', icon: Users },
      { name: 'Licenses', href: '/admin/licenses', icon: Key },
    ],
  },
  {
    section: 'Products',
    items: [
      { name: 'Apps & Features', href: '/admin/features', icon: Boxes },
      { name: 'Plans & Pricing', href: '/admin/plans', icon: CreditCard },
      { name: 'A/B Tests', href: '/admin/ab-tests', icon: FlaskConical, badge: 'Beta' },
    ],
  },
  {
    section: 'Configuration',
    items: [
      { name: 'Geography', href: '/admin/geography', icon: Globe },
      { name: 'Broadcasts', href: '/admin', icon: Megaphone },
      { name: 'Settings', href: '/admin/settings', icon: Settings },
    ],
  },
];

const SIDEBAR_COLLAPSED_KEY = 'lebusiness-admin-sidebar-collapsed';

export function AdminLayout({ children }: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });
  const { profile, signOut } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: '/login' });
  };

  const isActive = (href: string) => {
    if (href === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard';
    }
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-slate-950">
        {/* Mobile sidebar */}
        <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
          <div className="fixed inset-0 bg-black/80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-72 bg-slate-900 border-r border-slate-800">
            <div className="flex h-16 items-center justify-between px-6 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-white">Admin Panel</span>
              </div>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>
            <nav className="px-3 py-4 space-y-6">
              {adminNavigation.map((section) => (
                <div key={section.section}>
                  <h3 className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {section.section}
                  </h3>
                  <div className="space-y-1">
                    {section.items.map((item) => {
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={cn(
                            'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                            active
                              ? 'bg-violet-500/20 text-violet-400'
                              : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                          )}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="flex-1">{item.name}</span>
                          {item.badge && (
                            <Badge variant="secondary" className="text-xs bg-violet-500/20 text-violet-400">
                              {item.badge}
                            </Badge>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Desktop sidebar */}
        <div
          className={cn(
            'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col border-r border-slate-800 bg-slate-900 transition-all duration-300',
            isCollapsed ? 'lg:w-16' : 'lg:w-72'
          )}
        >
          {/* Header */}
          <div className={cn(
            'flex h-16 items-center border-b border-slate-800 transition-all duration-300',
            isCollapsed ? 'px-2 justify-center' : 'px-6 gap-3'
          )}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <span className="font-bold text-xl bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                  Admin Panel
                </span>
                <p className="text-xs text-slate-500">leBusiness ERP</p>
              </div>
            )}
          </div>

          {/* Back to App Link */}
          <div className={cn('px-3 py-3 border-b border-slate-800', isCollapsed && 'px-2')}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to="/dashboard"
                    className="flex items-center justify-center w-12 h-10 mx-auto rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Back to App</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to leBusiness
              </Link>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-6 overflow-y-auto">
            {adminNavigation.map((section) => (
              <div key={section.section}>
                {!isCollapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {section.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const active = isActive(item.href);

                    if (isCollapsed) {
                      return (
                        <Tooltip key={item.href}>
                          <TooltipTrigger asChild>
                            <Link
                              to={item.href}
                              className={cn(
                                'flex items-center justify-center w-12 h-12 mx-auto rounded-lg transition-colors relative',
                                active
                                  ? 'bg-violet-500/20 text-violet-400'
                                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                              )}
                            >
                              <item.icon className="w-5 h-5" />
                              {item.badge && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-violet-500 rounded-full" />
                              )}
                            </Link>
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            <p>{item.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                          active
                            ? 'bg-violet-500/20 text-violet-400'
                            : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                        )}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="flex-1">{item.name}</span>
                        {item.badge && (
                          <Badge variant="secondary" className="text-xs bg-violet-500/20 text-violet-400">
                            {item.badge}
                          </Badge>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Collapse toggle */}
          <div className="border-t border-slate-800 p-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={toggleCollapse}
                  className={cn(
                    'flex items-center justify-center w-full h-10 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors',
                    isCollapsed ? 'px-0' : 'px-3'
                  )}
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <>
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      <span className="text-sm">Collapse</span>
                    </>
                  )}
                </button>
              </TooltipTrigger>
              {isCollapsed && (
                <TooltipContent side="right">
                  <p>Expand sidebar</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>

          {/* User section */}
          <div className={cn('border-t border-slate-800 p-3', isCollapsed && 'p-2')}>
            {isCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleSignOut}
                    className="flex items-center justify-center w-12 h-12 mx-auto rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{profile?.fullName || 'Admin'}</p>
                  <p className="text-xs text-slate-400">Sign out</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {profile?.fullName || 'Admin'}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{profile?.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
                  onClick={handleSignOut}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className={cn(
          'transition-all duration-300',
          isCollapsed ? 'lg:pl-16' : 'lg:pl-72'
        )}>
          {/* Top header */}
          <div className="sticky top-0 z-40 flex h-16 bg-slate-900/95 backdrop-blur border-b border-slate-800">
            <button
              type="button"
              className="px-4 text-slate-400 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex flex-1 items-center justify-between px-4 sm:px-6">
              {/* Breadcrumb placeholder */}
              <div className="flex items-center gap-2 text-slate-400">
                <Shield className="w-5 h-5 text-violet-400" />
                <span className="text-sm font-medium text-white">Super Admin</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4">
                <ThemeToggle />
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
